import React from "react";
import { createAbsoluteUrl } from "utils/constants";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "app/components/ui/dialog";
import { Separator } from "app/components/ui/separator";
import { Button } from "app/components/ui/button";

interface DeviceNotCompatibleDialogProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  imeiNumber?: string | null;
  simNumber?: string | null;
  onClose?: () => void;
}

/** Format a SIM-style digit string with spaces every 4 digits. */
function formatSimDisplay(value: string | null | undefined): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
}

export const DeviceNotCompatibleDialog = ({
  showDialog,
  setShowDialog,
  imeiNumber,
  simNumber,
  onClose,
}: DeviceNotCompatibleDialogProps) => {
  const handleClose = () => {
    setShowDialog(false);
    onClose?.();
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent
        className="top-1/2 gap-0 bg-white w-[95%] max-w-[32.5rem] sm:max-w-[32.5rem] p-4 sm:p-5 lg:p-10 border-none rounded-2xl md:rounded-3xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogClose className="absolute top-5 right-5 rounded-full sm:top-[1.375rem] sm:right-[1.375rem] lg:top-[1.625rem] lg:right-[1.785rem] text-[#9CA3AF] hover:text-[#808080]">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="20"
            viewBox="0 0 15 20"
            fill="none"
          >
            <path
              d="M13.3818 5.88281C13.8701 5.39453 13.8701 4.60156 13.3818 4.11328C12.8936 3.625 12.1006 3.625 11.6123 4.11328L7.49902 8.23047L3.38184 4.11719C2.89355 3.62891 2.10059 3.62891 1.6123 4.11719C1.12402 4.60547 1.12402 5.39844 1.6123 5.88672L5.72949 10L1.61621 14.1172C1.12793 14.6055 1.12793 15.3984 1.61621 15.8867C2.10449 16.375 2.89746 16.375 3.38574 15.8867L7.49902 11.7695L11.6162 15.8828C12.1045 16.3711 12.8975 16.3711 13.3857 15.8828C13.874 15.3945 13.874 14.6016 13.3857 14.1133L9.26855 10L13.3818 5.88281Z"
              fill="currentColor"
            />
          </svg>
        </DialogClose>

        <DialogHeader className="justify-center items-center gap-0 sm:text-center p-0 bg-transparent border-none mb-[1.125rem] sm:mb-5 lg:mb-6">
          <div className="flex justify-center items-center bg-[#FEF2F2] size-[4.25rem] sm:size-[4.5rem] md:size-[4.75rem] lg:size-20 rounded-full p-2.5 mb-[1.125rem] sm:mb-5 lg:mb-6">
            <img
              src="/assets/error-triangle.svg"
              alt="Error"
              className="size-6 sm:size-7 md:size-8 lg:size-9"
            />
          </div>
          <DialogTitle className="font-mobile text-[#111827] text-2xl sm:text-[1.625rem] md:text-[1.75rem] lg:text-[1.875rem] font-bold leading-[1.875rem] sm:leading-8 md:leading-[2.125rem] lg:leading-[2.25rem] tracking-[-0.04688rem] mb-2 sm:mb-3 lg:mb-4">
            Device Not Compatible
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] text-sm sm:text-[0.925rem] lg:text-base font-normal leading-[1.375rem] sm:leading-6 lg:leading-[1.625rem] max-w-sm">
            We couldn&apos;t verify your device. Please try using a different
            device to continue.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 sm:gap-7 lg:gap-8 items-center text-center">
          {/* Details entered card */}
          {(imeiNumber || simNumber) && (
            <div className="w-full bg-[#F9FAFB] border border-[#F3F4F6] rounded-[0.625rem] md:rounded-xl p-4 sm:p-[1.125rem] lg:p-5">
              <p className="text-[#111827] text-xs sm:text-[0.8125rem] lg:text-sm font-semibold leading-4 sm:leading-[1.125rem] lg:leading-[1.25rem] tracking-[0.04375rem] uppercase mb-[1.125rem] sm:mb-5 lg:mb-6 text-left">
                Details Entered
              </p>
              <div className="space-y-2.5 sm:space-y-3">
                {imeiNumber && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="inline-block text-[#6B7280] text-xs sm:text-[0.8125rem] lg:text-sm font-normal leading-4 sm:leading-[1.125rem] lg:leading-5">
                      IMEI Number
                    </span>
                    <span className="inline-block font-liberation-mono text-[#111827] text-xs sm:text-[0.8125rem] lg:text-sm font-medium leading-4 sm:leading-[1.125rem] lg:leading-5 tracking-[-0.02188rem] tabular-nums">
                      {imeiNumber.replace(/\D/g, "")}
                    </span>
                  </div>
                )}
                <Separator className="bg-transparent border-b border-[#E5E7EBCC]" />
                {simNumber && (
                  <div className="flex justify-between items-center">
                    <span className="inline-block text-[#6B7280] text-xs sm:text-[0.8125rem] lg:text-sm font-normal leading-4 sm:leading-[1.125rem] lg:leading-5">
                      SIM Number
                    </span>
                    <span className="inline-block text-[#111827] font-liberation-mono text-xs sm:text-[0.8125rem] lg:text-sm font-medium leading-4 sm:leading-[1.125rem] lg:leading-5 tracking-[-0.02188rem] tabular-nums">
                      {formatSimDisplay(simNumber)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Close button */}
          {/* <Button
            type="button"
            variant="outline"
            className="w-full h-[3.25rem] sm:h-[3.375rem] md:h-[3.5rem] lg:h-[3.625rem] bg-white hover:bg-[#FAFAFA] px-4 sm:px-5 lg:px-6 py-3 sm:py-3.5 lg:py-4 border-2 border-[#E5E7EB] rounded-[0.75rem] text-[#374151] text-[0.925rem] sm:text-base lg:text-lg font-semibold leading-5 sm:leading-6 lg:leading-7"
            onClick={handleClose}
          >
            Close
          </Button> */}

          {/* Contact support link */}
          <div className="pt-4 sm:pt-5 lg:pt-6 border-t border-[#F3F4F6]">
            <p className="text-[#6B7280] text-xs sm:text-[0.8125rem] lg:text-sm font-normal leading-4 sm:leading-[1.125rem] lg:leading-5">
              Need help finding a compatible device?{" "}
              <Button
                type="button"
                variant="link"
                className="text-[#FE4D01] hover:text-[#E44501] text-xs sm:text-[0.8125rem] lg:text-sm font-semibold hover:underline leading-4 sm:leading-[1.125rem] lg:leading-5 decoration-[#E44501]"
                onClick={() => {
                  window.open(
                    createAbsoluteUrl("support-center"),
                    "_blank",
                    "noopener, noreferrer",
                  );
                }}
              >
                Contact Support
              </Button>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
